import React, {FC, ReactNode} from "react";
import {Button, Typography} from "antd";
import {SendOutlined} from "@ant-design/icons";
import {AccommodationSection} from "./AccommodationSection";
import {ActivityFields} from "./ActivityFields";
import {DynamicAnswerFields} from "./DynamicAnswerFields";
import {PrivacyConsentField} from "./FormFeedback";
import {ParticipantFields} from "./ParticipantFields";
import {ParticipantTypeProofField, ParticipationFeeField, PaymentMethodField} from "./ParticipationFields";
import {PayerFields} from "./PayerFields";
import {useRegistrationCopy} from "./registrationCopy";
import type {
    PayerTypeValue,
    PaymentMethodValue,
    PublicEventFormResponse,
    PublicRegistrationOption,
    PublicRoomAvailability
} from "./types";

// Individual registration sections, rendered inside the page's Form.
export const SingleRegistrationForm: FC<{
    eventForm: PublicEventFormResponse;
    payerType: PayerTypeValue;
    requiresParticipationFee: boolean;
    registrationOptions: PublicRegistrationOption[];
    showProofField: boolean;
    proofFileId?: number;
    proofFileName?: string;
    proofUploading: boolean;
    onProofUpload: (file: File) => void;
    showParticipationPayment: boolean;
    participationMethods: PaymentMethodValue[];
    reserveAccommodation: boolean;
    selectedRoom?: PublicRoomAvailability;
    accommodationEstimate: number;
    accommodationMethods: PaymentMethodValue[];
    submitting: boolean;
    submitErrorAlert: ReactNode;
    // Organizer registration: an existing-payer picker replaces the payer fields when provided,
    // and the dialog supplies its own submit button and does not collect GDPR consent.
    payerContent?: ReactNode;
    organizerMode?: boolean;
}> = ({
    eventForm,
    payerType,
    requiresParticipationFee,
    registrationOptions,
    showProofField,
    proofFileId,
    proofFileName,
    proofUploading,
    onProofUpload,
    showParticipationPayment,
    participationMethods,
    reserveAccommodation,
    selectedRoom,
    accommodationEstimate,
    accommodationMethods,
    submitting,
    submitErrorAlert,
    payerContent,
    organizerMode = false
}) => {
    const {labels} = useRegistrationCopy();
    const fields = eventForm.template?.fields ?? [];
    const rooms = eventForm.roomAvailability ?? [];
    const showParticipationSection = requiresParticipationFee || registrationOptions.length > 0;

    return (
        <>
            <section className="public-registration-form-section">
                <Typography.Title level={2}>{labels.participant}</Typography.Title>
                <Typography.Paragraph>{labels.participantHelp}</Typography.Paragraph>
                <ParticipantFields/>
            </section>

            {fields.length > 0 ? (
                <section className="public-registration-form-section">
                    <Typography.Title level={2}>{labels.registrationQuestions}</Typography.Title>
                    <DynamicAnswerFields fields={fields} namePrefix={["answers"]}/>
                </section>
            ) : null}

            <section className="public-registration-form-section">
                <Typography.Title level={2}>{labels.payer}</Typography.Title>
                <Typography.Paragraph>{labels.payerHelp}</Typography.Paragraph>
                {payerContent ?? <PayerFields payerType={payerType}/>}
            </section>

            {showParticipationSection ? (
                <section className="public-registration-form-section">
                    <Typography.Title level={2}>{labels.items}</Typography.Title>
                    <Typography.Paragraph>{labels.itemsHelp}</Typography.Paragraph>

                    {requiresParticipationFee ? <ParticipationFeeField fees={eventForm.participationFees ?? []}/> : null}

                    {showProofField ? (
                        <ParticipantTypeProofField
                            uploadedFileId={proofFileId}
                            uploadedFileName={proofFileName}
                            uploading={proofUploading}
                            submitting={submitting}
                            onUpload={onProofUpload}
                        />
                    ) : null}

                    {registrationOptions.length > 0 ? (
                        <ActivityFields
                            options={registrationOptions}
                            selectedOptionIdsName={["selectedOptionIds"]}
                            activityAnswersName={["activityAnswers"]}
                        />
                    ) : null}

                    {showParticipationPayment
                        ? <PaymentMethodField name="participationPaymentMethod" methods={participationMethods}/>
                        : null}
                </section>
            ) : null}

            {rooms.length > 0 ? (
                <AccommodationSection
                    rooms={rooms}
                    reserveAccommodation={reserveAccommodation}
                    selectedRoom={selectedRoom}
                    estimate={accommodationEstimate}
                    paymentMethods={accommodationMethods}
                />
            ) : null}

            <section className="public-registration-form-section">
                {submitErrorAlert}

                {organizerMode ? null : (
                    <>
                        <PrivacyConsentField/>

                        <div className="public-registration-submit-row">
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SendOutlined/>}
                                loading={submitting}
                                disabled={proofUploading}
                            >
                                {labels.submit}
                            </Button>
                        </div>
                    </>
                )}
            </section>
        </>
    );
};
